// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    updateCartCertificationsPerLine,
    UpdateCartCertificationsPerLineParameter,
} from "@eisinc/App/Services/updateCartCustomProperties";
import {
    createHandlerChainRunner,
    Handler,
    HasOnError,
    HasOnSuccess,
    makeHandlerChainAwaitable,
} from "@insite/client-framework/HandlerCreator";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCart";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";

type Parameter = UpdateCartCertificationsPerLineParameter & HasOnSuccess<any> & HasOnError<string>;

type HandlerType = Handler<Parameter, any>;

export const PopulateApiParameter: HandlerType = props => {
    const cartState = getCurrentCartState(props.getState()).value;
    if (!cartState) {
        throw new Error("There was no current cart while trying to update the current cart.");
    }
    props.apiParameter = {
        cartLineCertifications: props.parameter.cartLineCertifications,
    };
};

export const UpdateCart: HandlerType = async props => {
    props.apiResult = await updateCartCertificationsPerLine(props.apiParameter);
};

export const LoadCart: HandlerType = async props => {
    if (props.parameter.cartId !== API_URL_CURRENT_FRAGMENT) {
        const awaitableLoadCart = makeHandlerChainAwaitable(loadCart as any);
        await awaitableLoadCart({ cartId: props.parameter.cartId, shouldLoadFullCart: true })(
            props.dispatch,
            props.getState,
        );
    } else {
        const awaitableLoadCurrentCart = makeHandlerChainAwaitable(loadCurrentCart);
        await awaitableLoadCurrentCart({ getCartLines: true })(props.dispatch, props.getState);
    }
};

export const ExecuteOnErrorCallback: HandlerType = props => {
    if (!props.apiResult.successful) {
        props.parameter.onError?.(props.apiResult.errorMessage);
    }
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    if (props.apiResult.successful) {
        props.parameter.onSuccess?.(props.apiResult.result);
    }
};

export const chain = [PopulateApiParameter, UpdateCart, LoadCart, ExecuteOnErrorCallback, ExecuteOnSuccessCallback];
const updateCartLinesCertifications = createHandlerChainRunner(chain, "UpdateCartLinesCertifications");

export default updateCartLinesCertifications;
